/**
 * Collection Entity Schema
 *
 * Photo/media collections with sharing, embedding, and access control.
 */

import { z } from 'zod'

/**
 * Collection Type Enum (matches Prisma database enum)
 * For more specific categories, use GROUP type with sourceGroupId - the group's groupType provides the category
 */
export const CollectionTypeSchema = z.enum(['CUSTOM', 'SCHOOL', 'GROUP', 'SECTION', 'EVENT'])
export type CollectionType = z.infer<typeof CollectionTypeSchema>

/**
 * Extended Collection Type for UI display.
 *
 * `SECTION` is a first-class DB type backed by `sourceLeadSectionId` (added
 * in comms v2 U13). It is NOT a UI-only alias.
 *
 * The remaining display-only types (`GRADE_LEVEL`, `SCHOOL_YEAR`,
 * `SPORT_TEAM`, `CLUB`) are legacy UI categories that map to GROUP +
 * sourceGroupId at create time — any web mapping helper must keep that
 * behavior, and must map `SECTION → SECTION + sourceLeadSectionId` (NOT to
 * GROUP).
 */
export const CollectionTypeDisplaySchema = z.enum([
  'CUSTOM',
  'SCHOOL',
  'GROUP',
  'SECTION',
  'EVENT',
  // UI-only types that still map to GROUP + sourceGroupId in the database.
  'GRADE_LEVEL',
  'SCHOOL_YEAR',
  'SPORT_TEAM',
  'CLUB'
])
export type CollectionTypeDisplay = z.infer<typeof CollectionTypeDisplaySchema>

/**
 * Collection Visibility Enum
 */
export const CollectionVisibilitySchema = z.enum(['PUBLIC', 'MEMBERS_ONLY', 'PRIVATE'])
export type CollectionVisibility = z.infer<typeof CollectionVisibilitySchema>

/**
 * Collection Member Role Enum
 */
export const CollectionMemberRoleSchema = z.enum(['OWNER', 'EDITOR', 'CONTRIBUTOR', 'VIEWER'])
export type CollectionMemberRole = z.infer<typeof CollectionMemberRoleSchema>

/**
 * Embed Layout Enum
 */
export const EmbedLayoutSchema = z.enum(['GRID', 'MASONRY', 'SLIDESHOW', 'CAROUSEL'])
export type EmbedLayout = z.infer<typeof EmbedLayoutSchema>

/**
 * Media Type Enum
 */
export const MediaTypeSchema = z.enum(['PHOTO', 'VIDEO', 'AUDIO', 'DOCUMENT'])
export type MediaType = z.infer<typeof MediaTypeSchema>

/**
 * Collection Summary Schema
 * Minimal collection info for references
 */
export const CollectionSummarySchema = z.object({
  id: z.string().describe('Collection public ID'),
  name: z.string().describe('Collection name'),
  collectionType: CollectionTypeSchema.describe('Type of collection'),
  coverUrl: z.string().nullable().optional().describe('Cover image URL'),
  // SCH-2109: additive, for the album pickers. Absent from older responses and
  // from summaries that do not count.
  mediaCount: z.number().int().nonnegative().optional().describe('Number of approved media items')
})

export type CollectionSummary = z.infer<typeof CollectionSummarySchema>

/**
 * Collection Creator Schema
 */
export const CollectionCreatorSchema = z.object({
  id: z.string().describe('User public ID'),
  firstName: z.string().describe('First name'),
  lastName: z.string().describe('Last name'),
  photoUrl: z.string().nullable().optional().describe('Profile photo URL')
})

export type CollectionCreator = z.infer<typeof CollectionCreatorSchema>

/**
 * Collection Schema
 * Full collection info for list and detail views
 */
export const CollectionSchema = z.object({
  id: z.string().describe('Collection public ID'),
  name: z.string().describe('Collection name'),
  description: z.string().nullable().optional().describe('Collection description'),
  collectionType: CollectionTypeSchema.describe('Type of collection'),
  visibility: CollectionVisibilitySchema.describe('Visibility setting'),

  // Cover media
  coverMediaId: z.string().nullable().optional().describe('Cover media ID'),
  coverUrl: z.string().nullable().optional().describe('Cover image URL'),
  // Expanded cover media array (0-4 items: explicit cover or dynamic covers from collection)
  coverMedia: z
    .array(
      z.object({
        id: z.string(),
        url: z.string(),
        thumbnailUrl: z.string().nullable().optional(),
        mediaType: MediaTypeSchema,
        cloudinaryUrl: z.string().nullable().optional(),
        cloudinaryThumbnailUrl: z.string().nullable().optional(),
        // Mux playback id (SCH-1121) — additive; the only locator a video cover has.
        muxPlaybackId: z.string().nullable().optional()
      })
    )
    .optional()
    .describe('Cover media array (0-4 items)'),

  // Source links (XOR — at most one set; enforced via service-layer anchor guard)
  sourceGroupId: z.string().nullable().optional().describe('Linked group ID'),
  sourceLeadSectionId: z.string().nullable().optional().describe('Linked lead section ID'),
  sourceEventId: z.string().nullable().optional().describe('Linked event ID'),
  // External-source provenance (SCH-1931). Present only on collections a sync
  // mirrored from an outside album; user-created collections omit all three.
  sourcePlatformId: z.string().nullable().optional().describe('External album ID when mirrored from a source system'),
  allowDownloads: z.boolean().nullable().optional().describe('Source system download flag, captured and not enforced'),
  publishedAt: z.string().nullable().optional().describe('Source system publish date (ISO 8601)'),
  // Derived: true ONLY for a space's DEFAULT album — source-linked
  // (sourceGroupId/sourceLeadSectionId set) AND collectionType GROUP/SECTION.
  // User-created CUSTOM space albums are source-linked but stay deletable, so this
  // is false for them (R20). Default albums are undeletable (backend
  // `DEFAULT_COLLECTION_UNDELETABLE`); clients hide delete only when true
  // (SCH-142 / R16, R18, R20).
  isSourceLinked: z.boolean().optional().describe('Whether the collection is an undeletable default album'),
  isSpaceAnchored: z
    .boolean()
    .optional()
    .describe('Whether the collection is anchored on a space (either anchor kind, any type) — R8 client signal'),

  // Settings
  allowMemberUploads: z.boolean().describe('Whether members can upload'),
  allowMemberComments: z.boolean().describe('Whether members can comment'),
  requireApproval: z.boolean().describe('Whether uploads require approval'),
  allowedMediaTypes: z.array(MediaTypeSchema).nullable().optional().describe('Allowed media types'),

  // Embedding settings
  allowEmbedding: z.boolean().describe('Whether embedding is enabled'),
  embedLayout: EmbedLayoutSchema.optional().describe('Embed layout style'),
  embedThemeColor: z.string().nullable().optional().describe('Embed theme color'),
  embedShowMetadata: z.boolean().optional().describe('Show metadata in embed'),

  // Counts
  mediaCount: z.number().optional().describe('Total media items'),
  memberCount: z.number().optional().describe('Total members'),

  // Creator info
  createdByUser: CollectionCreatorSchema.optional().describe('Collection creator'),

  // Timestamps
  createdAt: z.coerce.date().describe('Creation timestamp'),
  updatedAt: z.coerce.date().describe('Last update timestamp')
})

export type Collection = z.infer<typeof CollectionSchema>

/**
 * Member user info object
 */
export const MemberUserInfoSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable().optional(),
  photoUrl: z.string().nullable().optional(),
  // Alias for photoUrl (backwards compatibility)
  profileImageUrl: z.string().nullable().optional()
})

/**
 * Collection Member Schema
 */
export const CollectionMemberSchema = z.object({
  id: z.string().describe('Membership ID'),
  userId: z.string().describe('User public ID'),
  role: CollectionMemberRoleSchema.describe('Member role'),

  // User info (flat)
  firstName: z.string().describe('First name'),
  lastName: z.string().describe('Last name'),
  email: z.string().nullable().optional().describe('Email'),
  photoUrl: z.string().nullable().optional().describe('Profile photo URL'),

  // User info (nested - for backwards compatibility)
  user: MemberUserInfoSchema.optional().describe('Nested user info'),

  // Permissions
  canUpload: z.boolean().describe('Can upload media'),
  canDelete: z.boolean().describe('Can delete media'),
  canManageMembers: z.boolean().describe('Can manage members'),
  canModerate: z.boolean().describe('Can moderate content'),

  // Notifications
  notifyOnNewMedia: z.boolean().describe('Notify on new media'),
  notifyOnComments: z.boolean().describe('Notify on comments'),
  notifyOnTags: z.boolean().describe('Notify on tags'),

  // Timestamps
  joinedAt: z.coerce.date().describe('When user joined')
})

export type CollectionMember = z.infer<typeof CollectionMemberSchema>

/**
 * Guest Access Link Schema
 */
export const GuestAccessLinkSchema = z.object({
  id: z.string().describe('Link ID'),
  collectionId: z.string().describe('Collection ID'),
  token: z.string().describe('Access token'),
  hasPassword: z.boolean().describe('Whether password protected'),
  expiresAt: z.coerce.date().nullable().optional().describe('Expiration date'),
  allowDownload: z.boolean().describe('Whether downloads allowed'),
  showMetadata: z.boolean().describe('Whether to show metadata'),
  viewCount: z.number().describe('Number of views'),
  isActive: z.boolean().describe('Whether link is active'),
  revokedAt: z.coerce.date().nullable().optional().describe('When link was revoked'),
  createdAt: z.coerce.date().describe('Creation timestamp'),
  // URL for sharing (computed field)
  shareUrl: z.string().optional().describe('Full share URL')
})

export type GuestAccessLink = z.infer<typeof GuestAccessLinkSchema>

/**
 * Request Link Schema (for photo uploads from external users)
 */
export const RequestLinkSchema = z.object({
  id: z.string().describe('Link ID'),
  collectionId: z.string().describe('Collection ID'),
  token: z.string().describe('Access token'),
  slug: z.string().nullable().optional().describe('Custom URL slug'),
  allowedMediaTypes: z.array(MediaTypeSchema).nullable().optional().describe('Allowed media types'),
  requireApproval: z.boolean().describe('Whether uploads require approval'),
  maxUploadsPerUser: z.number().nullable().optional().describe('Max uploads per user'),
  requireName: z.boolean().describe('Require uploader name'),
  requireEmail: z.boolean().describe('Require uploader email'),
  customMessage: z.string().nullable().optional().describe('Custom message'),
  expiresAt: z.coerce.date().nullable().optional().describe('Expiration date'),
  maxTotalUploads: z.number().nullable().optional().describe('Max total uploads'),
  totalUploads: z.number().describe('Current upload count'),
  uploadCount: z.number().optional().describe('Alias for totalUploads (backwards compatibility)'),
  viewCount: z.number().optional().describe('Number of views'),
  revokedAt: z.coerce.date().nullable().optional().describe('When link was revoked'),
  isActive: z.boolean().describe('Whether link is active'),
  createdAt: z.coerce.date().describe('Creation timestamp'),
  // URL for sharing (computed field)
  shareUrl: z.string().optional().describe('Full share URL')
})

export type RequestLink = z.infer<typeof RequestLinkSchema>
